import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { ScrollToTop } from "@/app/ScrollToTop";

// Always-in-bundle shell (small, renders immediately)
import { PublicLayout } from "@/components/layout/PublicLayout";
import { I18nProvider } from "@/i18n";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { Toaster } from "@/components/ui/toaster";
import { AgeGateModal } from "@/components/AgeGateModal";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";
import { AuthGuard } from "@/features/auth/components/AuthGuard";

// Route-level code splitting (fix for M-PERF-5). Each page is its own chunk
// — the homepage no longer ships the dashboard editor, react-easy-crop,
// react-dropzone, and @dnd-kit to every visitor.
const HomePage = lazy(() => import("@/pages/HomePage").then((m) => ({ default: m.HomePage })));
const ArtistPage = lazy(() => import("@/pages/ArtistPage").then((m) => ({ default: m.ArtistPage })));
const LoginPage = lazy(() => import("@/pages/LoginPage").then((m) => ({ default: m.LoginPage })));
const AboutPage = lazy(() => import("@/pages/AboutPage").then((m) => ({ default: m.AboutPage })));
const HowItWorksPage = lazy(() => import("@/pages/HowItWorksPage").then((m) => ({ default: m.HowItWorksPage })));
const ArtistGalleryPage = lazy(() => import("@/pages/ArtistGalleryPage").then((m) => ({ default: m.ArtistGalleryPage })));
const CommissionPage = lazy(() => import("@/pages/CommissionPage").then((m) => ({ default: m.CommissionPage })));
const TrackingPage = lazy(() => import("@/pages/TrackingPage").then((m) => ({ default: m.TrackingPage })));
const ReviewSubmissionPage = lazy(() => import("@/pages/ReviewSubmissionPage").then((m) => ({ default: m.ReviewSubmissionPage })));
const AdminReviewsPage = lazy(() => import("@/pages/admin/AdminReviewsPage").then((m) => ({ default: m.AdminReviewsPage })));

// Dashboard bundle (artist-facing; heavy — DnD kit, image cropper)
const DashboardLayout = lazy(() => import("@/components/layout/dashboard/Layout").then((m) => ({ default: m.Layout })));
const ProfileSection = lazy(() => import("@/features/profile/components/ProfileSection").then((m) => ({ default: m.ProfileSection })));
const PortfolioSection = lazy(() => import("@/features/portfolio/components/PortfolioSection").then((m) => ({ default: m.PortfolioSection })));
const CommissionsPage = lazy(() => import("@/features/commissions").then((m) => ({ default: m.CommissionsPage })));
const DashboardReviewsPage = lazy(() => import("@/features/reviews").then((m) => ({ default: m.DashboardReviewsPage })));
const SettingsSection = lazy(() => import("@/features/settings/components/SettingsSection").then((m) => ({ default: m.SettingsSection })));
const ComingSoonSection = lazy(() => import("@/components/common/ComingSoonSection").then((m) => ({ default: m.ComingSoonSection })));
const ClientAuthPage = lazy(() => import("@/features/client/ClientAuthPage").then((m) => ({ default: m.ClientAuthPage })));

function RouteFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <LoadingSpinner size="lg" />
    </div>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <I18nProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* Auth routes (outside layout) */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<PlaceholderPage name="Sign Up" />} />

              {/* Client routes (NEW - NOT protected) */}
              <Route path="/client/login" element={<ClientAuthPage />} />
              <Route path="/client/dashboard" element={<ClientAuthPage />} />
              <Route path="/client/favorites" element={<ClientAuthPage />} />

              {/* Dashboard routes (protected) */}
              <Route
                path="/dashboard/*"
                element={
                  <AuthGuard>
                    <DashboardLayout>
                      <ErrorBoundary>
                        <Routes>
                          <Route path="/" element={<Navigate to="/dashboard/profile" replace />} />
                          <Route path="/profile" element={<ProfileSection />} />
                          <Route path="/portfolio" element={<PortfolioSection />} />
                          <Route path="/commissions" element={<CommissionsPage />} />
                          <Route path="/reviews" element={<DashboardReviewsPage />} />
                          <Route path="/finances" element={<ComingSoonSection />} />
                          <Route path="/settings" element={<SettingsSection />} />
                        </Routes>
                      </ErrorBoundary>
                    </DashboardLayout>
                  </AuthGuard>
                }
              />

              {/* Gallery route (no layout - minimal design) */}
              <Route path="/artist/:slug/gallery" element={<ArtistGalleryPage />} />

              {/* YCH catalog retired (Oct 2026) — netlify.toml 301s these to / */}
              <Route path="/ych/*" element={<Navigate to="/" replace />} />

              {/* Commission form route (standalone - has its own layout) */}
              <Route path="/commission" element={<CommissionPage />} />
              <Route path="/commission/:artistSlug" element={<CommissionPage />} />

              {/* Review submission route (standalone - no auth required) */}
              <Route path="/review/:artistSlug/:token" element={<ReviewSubmissionPage />} />

              {/* Admin routes */}
              <Route
                path="/admin/reviews"
                element={
                  <AuthGuard>
                    <AdminReviewsPage />
                  </AuthGuard>
                }
              />

              {/* Public routes with layout */}
              <Route element={<PublicLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/how-it-works" element={<HowItWorksPage />} />
                <Route path="/artist/:slug" element={<ArtistPage />} />
                <Route path="/c/:token" element={<TrackingPage />} />
                <Route path="/queue/:slug" element={<PlaceholderPage name="Artist Queue" />} />
                <Route path="/approval/:token" element={<PlaceholderPage name="Approval" />} />
                <Route path="/payment/:token" element={<PlaceholderPage name="Payment" />} />
{/* Review route moved outside PublicLayout for standalone experience */}
              </Route>
            </Routes>
            </Suspense>
          </BrowserRouter>
          <AgeGateModal />
          <Toaster />
        </I18nProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

// Temporary placeholder for routes not yet implemented
function PlaceholderPage({ name }: { name: string }) {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-[var(--color-text)] mb-2">
          {name}
        </h1>
        <p className="text-[var(--color-text-muted)]">
          Coming in a future module
        </p>
      </div>
    </div>
  );
}
